'use client';

import Image from 'next/image';
import Link from 'next/link';
import { DocumentCheckIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import LogoLight from '@/../public/Arcanis_Logo_White.png';
import LogoDark from '@/../public/Arcanis_Logo_Dark.png';
import { Box } from '@mantine/core';
import { AnchorMenuLink } from '@/shared/AnchorMenuLink';
import { useMediaQuery } from '@mantine/hooks';
import { useDisclosure } from '@mantine/hooks';
import { Burger } from '@mantine/core';
import { ActionButton } from '@/shared/ActionButton';
import { LoginPopover } from '@/shared/LoginPopover';
import { useAppSelector } from '@/hooks/reduxHooks';
import { useRef, useState, useEffect } from 'react';
import { SelectedPage } from '@/types/types';
import { ExternalLink, PhoneIcon } from 'lucide-react';
/* import { ThemeSwitcher } from '@/features/themeSwitcher/ThemeSwitcher'; */

type Props = {};

const Navbar = (props: Props) => {
  const sectionLinks = [
    { label: 'Systematic Strategies', targetId: 'systematicstrategiesforventureinvesting' },
    { label: 'Our Selection Process', targetId: 'selectionprocess' },
    { label: 'The Future of Investment', targetId: 'thefutureofinvestment' },
    { label: 'Performance Comparison', targetId: 'performancecomparison' },
    { label: 'Systematic Investment Engine', targetId: 'systematicinvestmentengine' },
    { label: 'Contact Us', targetId: 'contactus' },
  ];

  const menuStructure = [
    { label: 'Home', href: '/', children: sectionLinks },
    { label: 'Arcanis Deep Research Collection', href: '/top-companies', children: [] },
  ];

  const isTopOfPage = useAppSelector((state) => state.menuLinks.isTopOfPage);

  const theme = useAppSelector((state) => state.theme.theme);
  const flexBetween = 'flex items-center justify-between';

  const [opened, { toggle, close }] = useDisclosure();
  const navBg =
    isTopOfPage && !opened
      ? ''
      : 'bg-[#ebeff0]/90 dark:bg-secondary-500/80 drop-shadow';

  const navRef = useRef<HTMLDivElement>(null);
  const [navHeight, setNavHeight] = useState(0);

  useEffect(() => {
    if (navRef.current) {
      setNavHeight(navRef.current.offsetHeight);
    }
  }, []);

  return (
    <nav>
      <Box className="relative" style={{ zIndex: 999 }}>
        <Box
          ref={navRef}
          className="fixed top-0 flex w-full flex-col"
          style={{ zIndex: 999 }}
        >
          <Box
            className={`${flexBetween} relative top-0 h-auto w-full whitespace-nowrap py-3`}
          >
            <Box className={`${flexBetween} mx-8 w-full xxs:max-md:mx-4`}>
              <Box className={`${flexBetween} w-full gap-16`}>
                {/* LEFT */}
                <Box className="flex w-full items-center gap-6">
                  <Burger
                    opened={opened}
                    onClick={toggle}
                    aria-label="Toggle navigation"
                    color={theme === 'light' ? '#4b677b' : '#FFF'}
                    className="z-50"
                  />
                  <Link href="/" className="xxs:max-md:hidden">
                    <Image
                      priority
                      src={theme === 'light' ? LogoDark : LogoLight}
                      alt="Arcanis Capital Logo"
                      className="h-6 w-auto -translate-y-1 object-cover transition hover:opacity-80"
                      quality={100}
                    />
                  </Link>
                </Box>
              </Box>
              {/* RIGHT */}
              <Box className="flex items-center justify-between gap-6 text-sm text-secondary-600 transition-transform duration-500 xxs:max-md:hidden dark:text-gray-50">
                {/* <ThemeSwitcher /> */}
                {/* <{LoginPopover>
                  <Box className="flex items-center justify-between gap-2 hover:text-primary-400">
                    <UserCircleIcon className="w-4" />
                    <p className="cursor-pointer">Sign In</p>
                  </Box>
                </LoginPopover>} */}
                {/* <div className="flex gap-2 items-center">
                  <ExternalLink className="w-4" />
                  <a href='https://arcanis.com' target="_blank" rel="noopener noreferrer" className="hover:text-primary-400 underline">
                    Research Hub
                  </a>
                </div> */}
                <button onClick={() => window.location.href = 'https://arcanis.com'} className='rounded-lg bg-gradient-to-r from-secondary-500 flex gap-2 items-center to-secondary-600 px-6 py-1.5 max-h-8 font-semibold text-gray-50 hover:from-secondary-300 hover:to-secondary-400 hover:text-gray-50'>
                  <ExternalLink className="w-4" />
                  Research Hub
                </button>
                <button onClick={() => window.location.href = '/top-companies'} className=' flex gap-2 items-center rounded-lg bg-gradient-to-r from-secondary-500 to-secondary-600 px-6 py-1.5 max-h-8 font-semibold text-gray-50 hover:from-secondary-300 hover:to-secondary-400 hover:text-gray-50'>
                  <DocumentCheckIcon className="w-4" />
                  Arcanis Collection
                </button>
                  <button onClick={() => window.location.href = 'https://calendly.com/arcanis/arcanis-intro'} className='flex gap-2 items-center  rounded-lg bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-1.5 max-h-8 font-semibold text-gray-50 hover:from-primary-300 hover:to-primary-400 hover:text-gray-50'>
                  <PhoneIcon className="w-4" />
                    Schedule a call
                </button>
              </Box>
            </Box>
            <Box
              id="animatedBox"
              className={`${navBg} absolute inset-0 -z-20`}
            ></Box>
          </Box>
        </Box>
        <Box
          id="burgerMenu"
          className={`fixed left-0 w-80 border-t-[1px] border-t-lightBlue-500/30 bg-[#ebeff0]/90 drop-shadow-xl transition-transform duration-500 dark:bg-secondary-500/90 ${opened ? 'translate-x-0' : '-translate-x-full'
            }`}
          style={{
            top: navHeight,
            height: `calc(100vh - ${navHeight}px)`,
            zIndex: 998,
          }}
        >
          <Box className="flex h-full flex-col gap-8 p-12">
            <Box className="flex flex-col gap-5 text-lg  text-secondary-600 dark:text-lightBlue-200">
              {menuStructure.map((item) => (
                <div key={item.label} className="flex flex-col gap-3">
                  <Link
                    href={item.href}
                    onClick={close}
                    className="transition hover:text-primary-400/90 dark:hover:text-primary-200"
                  >
                    {item.label}
                  </Link>
                  {item.children.length > 0 ? (
                    <Box className="flex flex-col gap-2 text-base text-secondary-500/90 dark:text-lightBlue-200/80">
                      {item.children.map((child) => (
                        <AnchorMenuLink
                          key={child.targetId}
                          page={child.label}
                          targetId={child.targetId}
                          isSubItem
                          onNavigate={close}
                        />
                      ))}
                    </Box>
                  ) : null}
                </div>
              ))}
            </Box>
            {/* <ThemeSwitcher /> */}
          </Box>
        </Box>
      </Box>
    </nav>
  );
};

export { Navbar };
